/**
 * The seller dashboard.
 *
 * Read-mostly on purpose. The whole proposition is that the seller does
 * nothing: we watch the ledgers, we detect the discrepancy, we file the claim.
 * Every control added here is a control someone has to be taught, chased about,
 * and blamed for not using — so there are none beyond the two asks the business
 * genuinely cannot proceed without.
 *
 * Note what is deliberately absent: the ops console's urgency colouring. A
 * seller cannot file, cannot chase TikTok and cannot shorten a window, so
 * showing them a queue turning red would create alarm about something they have
 * no lever on. The closing date is shown plainly. Urgency is our problem.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { getSellerFindings, type Classification, type SellerFinding } from '../api.js';
import { useAsync } from '../useAsync.js';
import { formatDate, formatMinor, NO_VALUE, pluralize } from '../format.js';
import { daysUntil, sortByUrgency } from '../urgency.js';
import { Badge, Callout, EmptyState, ErrorCallout, Loading } from '../ui.js';

/**
 * Classification, in the seller's language.
 *
 * `noise` never reaches them as "noise". It means we looked and there is no
 * claim — which is a real answer and worth being able to see, but it is not the
 * default view, because the default view is what we are doing for them.
 */
const STATUS_LABEL: Record<Classification, string> = {
  claimable: 'Being pursued',
  needs_review: 'Under review',
  noise: 'No claim',
};

export function SellerPage(): ReactNode {
  const { data, error, loading } = useAsync(() => getSellerFindings(), []);
  const [showAll, setShowAll] = useState(false);

  const asOf = data?.asOf ?? '';
  const findings = useMemo(
    () =>
      sortByUrgency(
        (data?.findings ?? []).map((finding) => ({
          ...finding,
          daysLeft: daysUntil(finding.expiresAt, asOf),
        })),
      ),
    [data, asOf],
  );

  const active = findings.filter((finding) => finding.status !== 'noise');
  const visible = showAll ? findings : active;
  const openValue = active.reduce((sum, finding) => sum + (finding.valueMinor ?? 0), 0);
  const unvalued = active.filter((finding) => finding.valueMinor === null).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Your recovery</h1>
          <p>
            What we have found in your FBT and finance ledgers, and what we are doing about it.
            {data ? ` Last updated ${formatDate(asOf)}.` : ''}
          </p>
        </div>
      </div>

      {error ? <ErrorCallout error={error} /> : null}
      {loading && !data ? <Loading label="Loading your findings" rows={3} /> : null}

      {data ? (
        <>
          <dl className="stats">
            <div className="stat">
              <dt>Open findings</dt>
              <dd>{active.length}</dd>
              <div className="stat-note">discrepancies we are pursuing or reviewing</div>
            </div>
            <div className="stat">
              <dt>Expected value</dt>
              <dd>{formatMinor(openValue)}</dd>
              <div className="stat-note">at your recorded cost basis, before TikTok decides</div>
            </div>
            <div className="stat">
              <dt>Recovered to date</dt>
              <dd>{NO_VALUE}</dd>
              <div className="stat-note">verified reimbursements appear here</div>
            </div>
            <div className="stat">
              <dt>Waiting on a cost</dt>
              <dd>{unvalued}</dd>
              <div className="stat-note">cannot be valued without your cost file</div>
            </div>
          </dl>

          <TwoAsks unvalued={unvalued} />
          <Recoveries />

          <section className="panel">
            <div className="panel-head">
              <h2>Findings</h2>
              <label className="field-hint">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={showAll}
                  onChange={(event) => setShowAll(event.target.checked)}
                />
                Include items we looked at and are not pursuing
              </label>
            </div>

            {visible.length === 0 ? (
              <EmptyState title="Nothing to show yet">
                Once your first full sync has run we list every discrepancy we find here, with what
                we expect to recover. Nothing is needed from you for that to happen.
              </EmptyState>
            ) : (
              <div className="table-scroll">
                <table>
                  <caption className="sr-only">
                    Discrepancies found in your ledgers and the status of each.
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Status</th>
                      <th scope="col">What we found</th>
                      <th scope="col">Shop</th>
                      <th scope="col" className="col-num">
                        Units
                      </th>
                      <th scope="col" className="col-num">
                        Expected value
                      </th>
                      <th scope="col" className="col-tight">
                        Claim window closes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((finding, index) => (
                      <tr key={`${rowKey(finding)}#${index}`}>
                        <td>
                          <Badge tone={finding.status === 'claimable' ? 'live' : undefined}>
                            {STATUS_LABEL[finding.status]}
                          </Badge>
                        </td>
                        <td>
                          {finding.claimLabel}
                          <div className="cell-detail mono">{finding.claimCode}</div>
                        </td>
                        <td>{finding.shopName ?? NO_VALUE}</td>
                        <td className="col-num">{finding.quantity}</td>
                        <td
                          className="col-num"
                          title={
                            finding.valueMinor === null
                              ? 'We hold no unit cost for this SKU, so we cannot put a number on it yet.'
                              : undefined
                          }
                        >
                          {formatMinor(finding.valueMinor)}
                        </td>
                        <td className="col-num col-tight">{formatDate(finding.expiresAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}

/**
 * The only two things ever asked of a seller.
 *
 * Stated as a closed list, because that promise is the product. A cost file,
 * because no finding can be valued without one and a claim filed at an inferred
 * value is a claim filed on a number we made up. And answers to evidence
 * requests, because a signed bill of lading or a carrier weight receipt exists
 * only with the seller or their freight forwarder — nobody else can produce it.
 */
function TwoAsks({ unvalued }: { unvalued: number }): ReactNode {
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>What we need from you</h2>
        <span className="field-hint">Two things, and never anything else.</span>
      </div>
      <div className="panel-body stack">
        <Callout tone={unvalued > 0 ? 'warn' : 'good'} title="1. A per-SKU cost file">
          <p>
            {unvalued > 0
              ? `${pluralize(unvalued, 'finding')} cannot be valued because we hold no unit cost for the SKU. TikTok's ledgers do not carry your cost, and we will never guess it from your selling price or value a claim at zero.`
              : 'Every open finding has a cost basis. Send an updated file whenever your unit costs change so new findings are valued correctly.'}
          </p>
          <p className="field-hint">
            Upload is handled by your account contact today. A CSV of SKU and unit cost is enough.
          </p>
        </Callout>

        <Callout tone="info" title="2. Answers to evidence requests">
          <p>
            Inbound and warehouse claims need documents only you, your supplier or your freight
            forwarder hold — a signed bill of lading, a commercial invoice, carrier proof of
            delivery, damage photos. When a claim needs one we will email you asking for that
            specific item and nothing more.
          </p>
          <p className="field-hint">
            No request is open right now. If one were, it would be listed here.
          </p>
        </Callout>
      </div>
    </section>
  );
}

/**
 * Recoveries.
 *
 * Empty by necessity rather than by design: there is no seller-facing endpoint
 * for verified recoveries or invoices yet — Phase 8 builds the matcher that
 * produces them. Rendering a fabricated or inferred number here would be worse
 * than an honest gap, since this is the figure the seller measures the whole
 * relationship by.
 */
function Recoveries(): ReactNode {
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Recoveries</h2>
      </div>
      <EmptyState title="No verified recoveries yet">
        A claim shows here once TikTok&rsquo;s reimbursement is visible in Finance, FBT Billing or a
        statement adjustment — not when it is approved. Approved and paid are different states, and
        only the second one is money.
      </EmptyState>
    </section>
  );
}

/**
 * A row identity, assembled because the endpoint sends no id.
 *
 * Not a stable key across syncs, but stable within one response, which is all
 * React needs. A finding id on the wire would be better.
 */
function rowKey(finding: SellerFinding): string {
  return `${finding.shopName}|${finding.claimCode}|${finding.quantity}|${finding.expiresAt}|${finding.valueMinor}`;
}
